import React, { useState, useEffect } from "react";
import { Menu, Icon, Label } from "semantic-ui-react";
import { Link, useLocation } from "react-router-dom";
import { useAuth, useSiteConfig, useOrder, usePolling } from "../../../hooks";
import { canCharge } from "../../../utils/constants";
import "./SideMenu.scss";

// Menú desplegable (☰ en TopMenu): se cierra al elegir una opción, con ESC o
// con clic fuera de él (overlay). los títulos de grupo ("Reportes",
// "Carta", "Configuración") solo despliegan su submenú, no cierran.
export function SideMenu(props) {
  const { children, open, onClose } = props;
  const { pathname } = useLocation();

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  // Clic en un enlace que navega (incluso el de la página actual, que no
  // cambia la ruta) → cerrar. Solo `a[href]`: Semantic también dibuja
  // los títulos de grupo como <a> (por tener onClick), pero sin href —
  // esos solo despliegan su submenú y no deben cerrar.
  const onMenuClick = (event) => {
    if (event.target.closest("a[href]")) onClose();
  };

  return (
    <div className={`side-menu-admin${open ? " side-menu-admin--open" : ""}`}>
      {open && <div className="side-menu-admin__overlay" onClick={onClose} />}
      <div className="side-menu-admin__drawer" onClick={onMenuClick}>
        <MenuLeft pathname={pathname} />
      </div>
      <div className="side-menu-admin__content">{children}</div>
    </div>
  );
}

// Menú agrupado (idea de Restaurant.pe): arriba la operación diaria
// (Pedidos, Reportes), luego la carta y la configuración. Acordeón: solo un
// grupo abierto a la vez (abrir uno cierra el otro); arranca abierto el grupo
// de la página actual.
// `show` filtra por rol (ver CLAUDE.md, "Roles del Admin/Trabajador").
const MENU = [
  { to: "/admin", icon: "home", text: "Pedidos" },
  // Venta rápida / WhatsApp / delivery (mesas virtuales), aparte del salón.
  // `badge`: cuántas tienen un pedido abierto (useExternalOpenCount).
  { to: "/admin/external", icon: "motorcycle", text: "Venta rápida y delivery", badge: "external" },
  // Pantalla de cocina por área (tablet/TV en cada área).
  { to: "/admin/kitchen", icon: "food", text: "Cocina" },
  // Pedidos (platos y bebidas) listos sin servir y cuánto llevan esperando (para quien dicta).
  { to: "/admin/to-serve", icon: "bell outline", text: "Listos para servir" },
  // Caja justo después de Pedidos, como en Restaurant.pe (Ventas → Caja).
  {
    key: "cash",
    icon: "calculator",
    text: "Caja",
    show: (me) => canCharge(me),
    items: [
      { to: "/admin/cash-charge", icon: "money bill alternate outline", text: "Cobrar" },
      { to: "/admin/cash", icon: "lock open", text: "Apertura y cierre" },
      { to: "/admin/cash-history", icon: "list alternate outline", text: "Cierres de caja" },
      {
        to: "/admin/comprobantes",
        icon: "file alternate outline",
        text: "Comprobantes",
        // Solo cuando el negocio activó la facturación (Configuración → Facturación).
        show: (me, config) => canCharge(me) && !!config?.invoicing_enabled,
      },
      {
        to: "/admin/tip-settlements",
        icon: "gift",
        text: "Liquidación de propinas",
        // Solo si la instalación registra propinas (Configuración → Propinas).
        show: (me, config) => canCharge(me) && !!config?.tips_enabled,
      },
    ],
  },
  {
    key: "reports",
    icon: "chart bar",
    text: "Reportes",
    show: (me) => me?.is_staff, // Admin/Súper Usuario: el Estándar atiende, no administra
    items: [
      { to: "/admin/dashboard", icon: "dashboard", text: "Panel del día" },
      { to: "/admin/payments-history", icon: "history", text: "Historial de Pagos" },
      { to: "/admin/daily-summary", icon: "calendar check outline", text: "Resumen Diario" },
      { to: "/admin/product-sales", icon: "chart line", text: "Ventas por Producto" },
      { to: "/admin/peak-hours", icon: "fire", text: "Horas Pico y Ocupación" },
      { to: "/admin/prep-times", icon: "clock outline", text: "Tiempos de Cocina" },
      { to: "/admin/losses", icon: "exclamation triangle", text: "Reporte de Pérdidas" },
      { to: "/admin/cancellations", icon: "ban", text: "Anulaciones y descuentos" },
    ],
  },
  {
    key: "menu",
    icon: "book",
    text: "Carta",
    show: (me) => me?.is_staff, // Admin/Súper Usuario: el Estándar atiende, no administra
    items: [
      { to: "/admin/categories", icon: "folder", text: "Categorías" },
      { to: "/admin/products", icon: "cart", text: "Productos" },
      // Se pueden apagar por instalación (Configuración → Módulos opcionales, Súper Usuario).
      {
        to: "/admin/extras",
        icon: "plus square outline",
        text: "Adicionales",
        show: (me, config) => config?.show_extras !== false,
      },
      {
        to: "/admin/offers",
        icon: "tags",
        text: "Ofertas",
        show: (me, config) => config?.show_offers !== false,
      },
    ],
  },
  {
    key: "settings",
    icon: "setting",
    text: "Configuración",
    show: (me) => me?.is_staff, // Admin/Súper Usuario: el Estándar atiende, no administra
    items: [
      { to: "/admin/tables", icon: "table", text: "Mesas" },
      { to: "/admin/customers", icon: "address book outline", text: "Clientes" },
      { to: "/admin/users", icon: "users", text: "Usuarios", show: (me) => me?.is_staff },
      { to: "/admin/site-config", icon: "image", text: "Logo", show: (me) => me?.is_staff },
      { to: "/admin/payment-qr", icon: "qrcode", text: "Pagos (Yape/Plin)", show: (me) => me?.is_staff },
      {
        to: "/admin/tips",
        icon: "gift",
        text: "Propinas",
        // Siempre para Admin: ahí está también el interruptor que las activa.
        show: (me) => me?.is_staff,
      },
      { to: "/admin/invoicing", icon: "file alternate outline", text: "Facturación", show: (me) => me?.is_staff },
      { to: "/admin/modules", icon: "toggle on", text: "Módulos opcionales", show: (me) => me?.is_superuser },
      { to: "/admin/backup", icon: "cloud download", text: "Respaldo", show: (me) => me?.is_superuser },
    ],
  },
  { to: "/admin/help", icon: "question circle outline", text: "Ayuda" },
];

function MenuLeft(props) {
  const { pathname } = props;
  const { auth } = useAuth();
  const { siteConfig, getSiteConfig } = useSiteConfig();
  const [openGroup, setOpenGroup] = useState(
    () =>
      MENU.find((entry) => entry.items?.some((item) => item.to === pathname))
        ?.key || null
  );

  useEffect(() => {
    getSiteConfig();
  }, []);

  const toggleGroup = (key) =>
    setOpenGroup((prev) => (prev === key ? null : key));

  const externalOpen = useExternalOpenCount();

  const renderLink = (item) => (
    <Menu.Item
      key={item.to}
      as={Link}
      to={item.to}
      active={pathname === item.to}
    >
      <Icon name={item.icon} /> {item.text}
      {item.badge === "external" && externalOpen > 0 && (
        <Label circular color="orange" size="mini" className="side__badge">
          {externalOpen}
        </Label>
      )}
    </Menu.Item>
  );

  return (
    <Menu fixed="left" borderless className="side" vertical>
      {/* Cabecera del menú (idea tomada de Restaurant.pe) = logo 2: la
          marca del negocio y quién está conectado. La del desarrollador
          (logo 1) va en la barra superior. */}
      <div className="side__header">
        {siteConfig?.logo && <img src={siteConfig.logo} alt="Logo" />}
        {siteConfig?.site_name && (
          <p className="side__header-site">{siteConfig.site_name}</p>
        )}
        <p className="side__header-user">
          Usuario: {auth.me?.first_name || auth.me?.username || auth.me?.email}
        </p>
      </div>

      {MENU.map((entry) => {
        if (!entry.items) return renderLink(entry);

        if (entry.show && !entry.show(auth.me)) return null;
        const items = entry.items.filter((item) => !item.show || item.show(auth.me, siteConfig));
        if (items.length === 0) return null;
        const isOpen = openGroup === entry.key;

        return (
          <React.Fragment key={entry.key}>
            <Menu.Item onClick={() => toggleGroup(entry.key)}>
              <Icon name={entry.icon} /> {entry.text}
              <Icon
                name={isOpen ? "angle up" : "angle down"}
                style={{ float: "right" }}
              />
            </Menu.Item>
            {isOpen && (
              <Menu.Menu className="side__submenu">{items.map(renderLink)}</Menu.Menu>
            )}
          </React.Fragment>
        );
      })}

    </Menu>
  );
}

// Mesas de venta rápida / WhatsApp / delivery con algún pedido abierto (en
// cocina, listo o por cobrar): el número al lado de "Venta rápida y
// delivery", para verlos sin salir del salón. Se refresca cada 15 s.
function useExternalOpenCount() {
  const { getOpenOrders } = useOrder();
  const [count, setCount] = React.useState(0);
  usePolling(
    async () => {
      try {
        const orders = await getOpenOrders();
        const tables = new Set(
          (orders || [])
            .filter((order) => order.table && order.table_data?.kind && order.table_data.kind !== "SALON")
            .map((order) => order.table)
        );
        setCount(tables.size);
      } catch (error) {
        // sin conexión: se mantiene el último valor
      }
    },
    15000,
    { immediate: true }
  );
  return count;
}
